<template>
  <div class="cr-page bq-history-page">
    <SnackbarComp :SnackbarComponent="snackbar" />

    <div class="cr-panel bq-history-page__panel">
      <div class="cr-panel__toolbar bq-history-page__toolbar">
        <div class="cr-panel__toolbar-start">
          <v-btn
            variant="text"
            size="small"
            class="bq-history-page__back"
            prepend-icon="mdi-arrow-left"
            @click="goBack"
          >
            Back to Leads
          </v-btn>
          <span class="bq-history-page__count">
            {{ visibleUploads.length }}
            upload{{ visibleUploads.length === 1 ? '' : 's' }}
          </span>
        </div>

        <div class="bq-history-page__toolbar-end">
          <v-select
            v-model="contextFilter"
            :items="contextFilterOptions"
            item-title="title"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            label="Product"
            :loading="contextsLoading"
            class="bq-history-page__context-filter"
          />
          <v-btn
            variant="outlined"
            size="small"
            class="bq-history-page__refresh"
            prepend-icon="mdi-refresh"
            :loading="historyLoading"
            @click="loadHistory()"
          >
            Refresh
          </v-btn>
        </div>
      </div>

      <div class="bq-history-page__body">
        <header class="bq-history-page__masthead">
          <div class="bq-history-page__masthead-mark" aria-hidden="true">
            <v-icon icon="mdi-file-upload-outline" size="22" />
          </div>
          <div class="bq-history-page__masthead-copy">
            <h2 class="bq-history-page__title">Bulk upload history</h2>
            <p class="bq-history-page__subtitle">
              Track CSV lead imports — in-progress uploads refresh automatically.
            </p>
          </div>
        </header>

        <div
          v-if="historyLoading && !visibleUploads.length"
          class="bq-history-page__state"
        >
          <v-progress-circular indeterminate size="28" width="2" color="primary" />
          <p>Loading upload history…</p>
        </div>

        <div v-else-if="!visibleUploads.length" class="bq-history-page__state">
          <div class="bq-history-page__state-icon" aria-hidden="true">
            <v-icon icon="mdi-history" size="32" />
          </div>
          <p class="bq-history-page__state-title">No bulk uploads yet</p>
          <p class="bq-history-page__state-sub">
            Use Bulk Upload on the Leads page to import a CSV of leads.
          </p>
        </div>

        <div v-else class="bq-history-page__list">
          <article
            v-for="upload in visibleUploads"
            :key="upload.upload_id"
            class="bq-history-card"
            :class="`bq-history-card--${statusTone(upload.status)}`"
          >
            <header class="bq-history-card__head">
              <div
                class="bq-history-card__file"
                :title="upload.upload_id || undefined"
              >
                <span class="bq-history-card__file-badge" aria-hidden="true">
                  <v-icon icon="mdi-file-delimited-outline" size="20" />
                </span>
                <div class="bq-history-card__file-copy">
                  <h3 class="bq-history-card__name" :title="upload.filename">
                    {{ upload.filename || 'CSV upload' }}
                  </h3>
                  <p class="bq-history-card__meta">
                    <span v-if="formatUploadDate(upload.created_on)">
                      {{ formatUploadDate(upload.created_on) }}
                    </span>
                    <template v-if="uploadContextNames(upload)">
                      <span class="bq-history-card__dot" aria-hidden="true" />
                      <span>{{ uploadContextNames(upload) }}</span>
                    </template>
                    <template v-if="uploadUploaderLabel(upload)">
                      <span class="bq-history-card__dot" aria-hidden="true" />
                      <span>by {{ uploadUploaderLabel(upload) }}</span>
                    </template>
                  </p>
                </div>
              </div>

              <span
                class="bq-history-card__status"
                :class="`bq-history-card__status--${statusTone(upload.status)}`"
              >
                <v-progress-circular
                  v-if="isInProgress(upload.status)"
                  indeterminate
                  size="12"
                  width="2"
                />
                {{ upload.status || 'PENDING' }}
              </span>
            </header>

            <template v-if="!isInProgress(upload.status)">
              <div class="bq-history-card__metrics">
                <div class="bq-history-metric">
                  <span class="bq-history-metric__label">Total rows</span>
                  <strong class="bq-history-metric__value">
                    {{ upload.total_rows ?? 0 }}
                  </strong>
                </div>
                <div class="bq-history-metric bq-history-metric--success">
                  <span class="bq-history-metric__label">Succeeded</span>
                  <strong class="bq-history-metric__value">
                    {{ upload.success_count ?? 0 }}
                  </strong>
                </div>
                <div class="bq-history-metric bq-history-metric--error">
                  <span class="bq-history-metric__label">Failed</span>
                  <strong class="bq-history-metric__value">
                    {{ upload.error_count ?? 0 }}
                  </strong>
                </div>
              </div>

              <div
                v-if="Number(upload.total_rows) > 0"
                class="bq-history-card__bar"
                role="img"
                :aria-label="`${upload.success_count ?? 0} succeeded, ${upload.error_count ?? 0} failed of ${upload.total_rows} rows`"
              >
                <span
                  class="bq-history-card__bar-success"
                  :style="{ width: `${successPct(upload)}%` }"
                />
                <span
                  class="bq-history-card__bar-fail"
                  :style="{ width: `${failPct(upload)}%` }"
                />
              </div>
            </template>

            <div
              v-else
              class="bq-history-card__processing"
            >
              <v-progress-linear
                indeterminate
                color="primary"
                height="3"
                rounded
              />
              <span>Import in progress…</span>
            </div>

            <div
              v-if="Array.isArray(upload.errors) && upload.errors.length"
              class="bq-history-errors"
              :class="{ 'is-open': isErrorsOpen(upload.upload_id) }"
            >
              <button
                type="button"
                class="bq-history-errors__toggle"
                :aria-expanded="isErrorsOpen(upload.upload_id)"
                @click="toggleErrors(upload.upload_id)"
              >
                <v-icon icon="mdi-alert-circle-outline" size="18" />
                <span>Show errors ({{ upload.errors.length }})</span>
                <v-icon
                  class="bq-history-errors__chevron"
                  :icon="isErrorsOpen(upload.upload_id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  size="18"
                />
              </button>
              <ul v-show="isErrorsOpen(upload.upload_id)" class="bq-history-errors__list">
                <li
                  v-for="(err, index) in upload.errors"
                  :key="index"
                  class="bq-history-errors__item"
                >
                  <span class="bq-history-errors__row">Row {{ err.row }}</span>
                  <span class="bq-history-errors__msg">
                    <template v-if="err.context_name">{{ err.context_name }} · </template>
                    {{ err.error }}
                  </span>
                </li>
              </ul>
            </div>

            <footer
              v-if="Number(upload.success_count) > 0 && !isInProgress(upload.status)"
              class="bq-history-card__actions"
            >
              <v-menu
                v-if="(upload.context_ids || []).length > 1"
                location="bottom start"
                offset="4"
              >
                <template #activator="{ props: menuProps }">
                  <button
                    type="button"
                    class="bq-history-card__btn bq-history-card__btn--primary"
                    v-bind="menuProps"
                  >
                    <v-icon icon="mdi-account-search-outline" size="16" />
                    View leads
                    <v-icon icon="mdi-chevron-down" size="16" />
                  </button>
                </template>
                <v-list density="compact">
                  <v-list-item
                    v-for="(contextId, index) in upload.context_ids"
                    :key="contextId"
                    :title="upload.context_names?.[index] || contextNameById(contextId)"
                    @click="viewUploadLeads(upload, contextId)"
                  />
                </v-list>
              </v-menu>
              <button
                v-else
                type="button"
                class="bq-history-card__btn bq-history-card__btn--primary"
                @click="viewUploadLeads(upload)"
              >
                <v-icon icon="mdi-account-search-outline" size="16" />
                View leads
              </button>

              <button
                v-if="upload.deleted_on"
                type="button"
                class="bq-history-card__btn"
                disabled
              >
                Leads deleted
              </button>
              <button
                v-else
                type="button"
                class="bq-history-card__btn bq-history-card__btn--danger"
                @click="askDeleteUploadLeads(upload)"
              >
                <v-icon icon="mdi-trash-can-outline" size="16" />
                Delete leads
              </button>
            </footer>
          </article>

          <button
            v-if="hasMore"
            type="button"
            class="bq-history-page__load-more"
            :disabled="historyLoading"
            @click="loadHistory({ append: true })"
          >
            <v-progress-circular
              v-if="historyLoading"
              indeterminate
              size="14"
              width="2"
            />
            {{ historyLoading ? 'Loading…' : 'Load more' }}
          </button>
        </div>
      </div>
    </div>

    <v-dialog v-model="showDeleteConfirm" max-width="440" persistent>
      <v-card rounded="xl" class="bq-history-confirm">
        <div class="bq-history-confirm__head">
          <span class="bq-history-confirm__icon" aria-hidden="true">
            <v-icon icon="mdi-trash-can-outline" size="22" />
          </span>
          <div>
            <h3 class="bq-history-confirm__title">Delete uploaded leads?</h3>
            <p class="bq-history-confirm__sub">This action cannot be undone.</p>
          </div>
        </div>
        <v-card-text class="bq-history-confirm__body">
          This deletes every lead created or last updated by
          <strong>{{ uploadToDelete?.filename || 'this upload' }}</strong>
          ({{ uploadToDelete?.success_count ?? 0 }} lead{{
            Number(uploadToDelete?.success_count) === 1 ? '' : 's'
          }}).
        </v-card-text>
        <v-card-actions class="bq-history-confirm__actions">
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="deletingUpload"
            @click="showDeleteConfirm = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="deletingUpload"
            @click="confirmDeleteUploadLeads"
          >
            Delete leads
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import { listBulkLeadUploads, deleteBulkUploadLeads } from '@/api/leadsApi'
import { fetchContexts } from '@/api/contextsApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { unwrapApiList } from '@/utils/graphql'
import { normalizeContextsList } from '@/utils/contexts'
import { formatDateTime } from '@/utils/format'
import '@/styles/business-queries/bulk-upload-history.css'

defineOptions({ name: 'BulkUploadHistoryPage' })

const POLL_INTERVAL_MS = 5000

const router = useRouter()
const store = useStore()

const snackbar = ref({})
const uploads = ref([])
const historyLoading = ref(false)
const nextToken = ref('')
const hasMore = ref(false)
const contexts = ref([])
const contextsLoading = ref(false)
const contextFilter = ref('')
const showDeleteConfirm = ref(false)
const uploadToDelete = ref(null)
const deletingUpload = ref(false)
const openErrorIds = ref(new Set())
let pollTimer = null

const isSessionReady = computed(() => store.getters.isSessionReady)

const contextFilterOptions = computed(() => [
  { title: 'All products', value: '' },
  ...contexts.value.map((context) => ({
    title: context.context_name,
    value: context.context_id,
  })),
])

function contextNameById(contextId) {
  const match = contexts.value.find(
    (context) => context.context_id === contextId,
  )
  return match?.context_name || contextId
}

// A PENDING row older than the presigned URL's 1-hour lifetime means the
// PUT never happened (expired URL / abandoned upload) — hide it.
const STALE_PENDING_MS = 60 * 60 * 1000

function isStalePending(upload) {
  if (String(upload?.status ?? '').toUpperCase() !== 'PENDING') return false
  const createdOn = new Date(upload?.created_on ?? '').getTime()
  if (!Number.isFinite(createdOn)) return false
  return Date.now() - createdOn > STALE_PENDING_MS
}

const visibleUploads = computed(() =>
  uploads.value.filter((upload) => !isStalePending(upload)),
)

async function loadContexts() {
  const orgId = String(store.getters.sessionContext?.org_id || '').trim()
  if (!orgId) return

  contextsLoading.value = true
  try {
    const response = await fetchContexts({ org_id: orgId })
    contexts.value = isApiSuccess(response)
      ? normalizeContextsList(unwrapApiList(response))
      : []
  } catch {
    contexts.value = []
  } finally {
    contextsLoading.value = false
  }
}

function normalizeHistoryData(response) {
  const data = response?.data ?? response
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    next_token: String(data?.next_token ?? '').trim(),
    has_more: Boolean(data?.has_more),
  }
}

async function loadHistory({ append = false } = {}) {
  historyLoading.value = true
  try {
    const response = await listBulkLeadUploads({
      limit: 20,
      ...(contextFilter.value ? { context_id: contextFilter.value } : {}),
      ...(append && nextToken.value ? { next_token: nextToken.value } : {}),
    })
    if (!isApiSuccess(response)) {
      snackbar.value = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: getApiMessage(response, 'Failed to load upload history'),
      }
      return
    }

    const { items, next_token, has_more } = normalizeHistoryData(response)
    uploads.value = append ? [...uploads.value, ...items] : items
    nextToken.value = next_token
    hasMore.value = has_more
    schedulePollIfNeeded()
  } finally {
    historyLoading.value = false
  }
}

function isInProgress(status) {
  const value = String(status ?? '').toUpperCase()
  return value === 'PENDING' || value === 'PROCESSING'
}

function statusTone(status) {
  const value = String(status ?? '').toUpperCase()
  if (value === 'COMPLETED') return 'success'
  if (value === 'FAILED') return 'error'
  return 'progress'
}

function uploadContextNames(upload) {
  const names = Array.isArray(upload?.context_names) ? upload.context_names : []
  return names.filter(Boolean).join(', ')
}

/** Hide raw UUIDs from the meta line — keep readable names/emails only. */
function uploadUploaderLabel(upload) {
  const value = String(upload?.uploaded_by || '').trim()
  if (!value) return ''
  const uuidLike =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value,
    )
  if (uuidLike) return ''
  return value
}

function formatUploadDate(value) {
  return value ? formatDateTime(value) : ''
}

function successPct(upload) {
  const total = Number(upload?.total_rows) || 0
  if (total <= 0) return 0
  const success = Math.max(0, Number(upload?.success_count) || 0)
  return Math.min(100, (success / total) * 100)
}

function failPct(upload) {
  const total = Number(upload?.total_rows) || 0
  if (total <= 0) return 0
  const failed = Math.max(0, Number(upload?.error_count) || 0)
  return Math.min(100, (failed / total) * 100)
}

function isErrorsOpen(uploadId) {
  return openErrorIds.value.has(uploadId)
}

function toggleErrors(uploadId) {
  const next = new Set(openErrorIds.value)
  if (next.has(uploadId)) next.delete(uploadId)
  else next.add(uploadId)
  openErrorIds.value = next
}

function stopPolling() {
  if (pollTimer) {
    clearTimeout(pollTimer)
    pollTimer = null
  }
}

function schedulePollIfNeeded() {
  stopPolling()
  if (!uploads.value.some((upload) => isInProgress(upload.status))) return

  pollTimer = setTimeout(() => {
    void loadHistory()
  }, POLL_INTERVAL_MS)
}

function goBack() {
  router.push({ name: 'BusinessQueries' })
}

/**
 * Jump to the lead list pre-filtered to this upload. The Leads page applies
 * upload_id as a client-side filter against its loaded lead set.
 */
function viewUploadLeads(upload, contextId = null) {
  const contextIds = Array.isArray(upload?.context_ids) ? upload.context_ids : []
  const targetContextId = contextId || contextIds[0]
  router.push({
    name: 'BusinessQueries',
    query: {
      upload_id: upload?.upload_id || '',
      ...(targetContextId ? { context_id: targetContextId } : {}),
    },
  })
}

function askDeleteUploadLeads(upload) {
  uploadToDelete.value = upload
  showDeleteConfirm.value = true
}

async function confirmDeleteUploadLeads() {
  const uploadId = String(uploadToDelete.value?.upload_id || '').trim()
  if (!uploadId) return

  deletingUpload.value = true
  try {
    const response = await deleteBulkUploadLeads({ upload_id: uploadId })
    if (!isApiSuccess(response)) {
      snackbar.value = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: getApiMessage(response, 'Failed to delete uploaded leads'),
      }
      return
    }

    const deletedCount = Number(response?.data?.deleted_count)
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'success',
      SnackbarText: getApiMessage(
        response,
        Number.isFinite(deletedCount)
          ? `Deleted ${deletedCount} lead${deletedCount === 1 ? '' : 's'}.`
          : 'Uploaded leads deleted.',
      ),
    }
    showDeleteConfirm.value = false
    uploadToDelete.value = null
    void loadHistory()
  } finally {
    deletingUpload.value = false
  }
}

watch(
  isSessionReady,
  (ready) => {
    if (!ready) return
    void loadHistory()
    void loadContexts()
  },
  { immediate: true },
)

watch(contextFilter, () => {
  nextToken.value = ''
  hasMore.value = false
  void loadHistory()
})

onBeforeUnmount(stopPolling)
</script>
